import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Paper, Typography, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Snackbar, Alert,
    CircularProgress, FormControl, InputLabel, Select, MenuItem, Chip, TablePagination,
} from "@mui/material";
import { Add as AddIcon, Refresh as RefreshIcon, LocationOn as LocationOnIcon, Edit as EditIcon, Close as CloseIcon } from "@mui/icons-material";
import LookupService from "../service/LookupService";

const LocationManagement = () => {
const [locations, setLocations] = useState([]);
const [loading, setLoading] = useState(false);
const [saving, setSaving] = useState(false);
const [code, setCode] = useState("");
const [name, setName] = useState("");
const [status, setStatus] = useState("A");
const [editingCode, setEditingCode] = useState(null);
const [tableSearch, setTableSearch] = useState("");
const [statusFilter, setStatusFilter] = useState("all"); // "all" | "A" | "I"
const [page, setPage] = useState(0);
const rowsPerPage = 10;
const [alert, setAlert] = useState({ open: false, severity: "success", message: "" });
const normalize = (item) => ({
    code: item.locCode || item.LocationCode || item.locationCode || item.Code || item.code || "",
    name: item.locName || item.LocationName || item.locationName || item.Name || item.name || "",
    status: item.Status || item.status || "A",
});

const loadLocations = useCallback(async () => {
    setLoading(true);
    try {
const data = await LookupService.getLocations();
    const list = Array.isArray(data) ? data : data?.resultSet || data?.data || [];
    setLocations(list.map(normalize));
    } catch (error) {
    setAlert({ open: true, severity: "error", message: "Failed to load locations." });
    } finally {
    setLoading(false);
    }
}, []);

useEffect(() => { loadLocations(); }, [loadLocations]);
useEffect(() => { setPage(0); }, [tableSearch, statusFilter]);

const handleSelectRow = (loc) => {
    setEditingCode(loc.code);
    setCode(loc.code);
    setName(loc.name);
    setStatus(loc.status === "I" ? "I" : "A");
};

const handleCancelEdit = () => {
    setEditingCode(null);
    setCode(""); setName(""); setStatus("A");
};

const handleAdd = async () => {
    const trimmedCode = code.trim();
    const trimmedName = name.trim();
    if (!trimmedCode || !trimmedName) {
    setAlert({ open: true, severity: "error", message: "Both code and name are required." });
    return;
    }
    const isEditing = editingCode !== null;
    setSaving(true);
    try {
    const payload = [{ LocationCode: trimmedCode, LocationName: trimmedName, Status: status }];
    await LookupService.postLocation(payload);
    setAlert({ open: true, severity: "success", message: isEditing ? "Location updated." : "Location added." });
    setEditingCode(null);
    setCode(""); setName(""); setStatus("A");
    await loadLocations();
    } catch (error) {
    setAlert({ open: true, severity: "error", message: isEditing ? "Failed to update location." : "Failed to add location." });
    } finally {
    setSaving(false);
    }
};

const filteredLocations = locations
    .filter((loc) => statusFilter === "all" || loc.status === statusFilter)
    .filter((loc) =>
        !tableSearch.trim() ||
        loc.code.toLowerCase().includes(tableSearch.trim().toLowerCase()) ||
        loc.name.toLowerCase().includes(tableSearch.trim().toLowerCase())
    );

return (
    <Box sx={{ width: "100%", maxWidth: 800, mx: "auto", mt: 4, px: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <LocationOnIcon sx={{ color: "#142c54", mr: 1 }} />
        <Typography variant="h5" fontWeight={700} color="#142c54">Locations</Typography>
    </Box>

    <Paper sx={{ p: 3, mb: 3 }}>
        {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add locations available for the "Work Location" dropdown on the Employee Register form.
        </Typography> */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField size="small" label="Code" value={code} onChange={(e) => setCode(e.target.value)} sx={{ width: 140 }} disabled={editingCode !== null} />
        <TextField size="small" label="Location name" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 1, minWidth: 200 }} />
        <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="A">Active</MenuItem>
            <MenuItem value="I">Inactive</MenuItem>
            </Select>
        </FormControl>
        <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (editingCode !== null ? <EditIcon /> : <AddIcon />)}
            onClick={handleAdd}
            disabled={saving}
            sx={{ backgroundColor: "#142c54", whiteSpace: "nowrap" }}
        >
            {editingCode !== null ? "Update" : "Add"}
        </Button>
        {editingCode !== null && (
            <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleCancelEdit} disabled={saving} sx={{ whiteSpace: "nowrap" }}>
            Cancel
            </Button>
        )}
        </Box>
    </Paper>

    <Paper>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: 2, gap: 2, flexWrap: "wrap" }}>
        <Typography variant="subtitle2" color="text.secondary">
            {filteredLocations.length} of {locations.length} location{locations.length === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="A">Active only</MenuItem>
                <MenuItem value="I">Inactive only</MenuItem>
            </Select>
            </FormControl>
            <TextField
            size="small"
            placeholder="Search by code or name..."
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            sx={{ width: 220 }}
            />
            <IconButton size="small" onClick={loadLocations} disabled={loading}>
            <RefreshIcon fontSize="small" />
            </IconButton>
        </Box>
        </Box>
        <TableContainer>
        <Table stickyHeader>
            <TableHead>
            <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
            </TableHead>
            <TableBody>
            {loading ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>
            ) : filteredLocations.length === 0 ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ color: "text.secondary", py: 4 }}>No locations found.</TableCell></TableRow>
            ) : (
                filteredLocations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((loc, idx) => (
                <TableRow
                    key={`${loc.code}-${idx}`}
                    hover
                    onClick={() => handleSelectRow(loc)}
                    selected={editingCode === loc.code}
                    sx={{
                            cursor: "pointer",
                            animation: "rowFade 0.25s ease",
                            "@keyframes rowFade": { from: { opacity: 0 }, to: { opacity: 1 } },
                        }}
                >
                    <TableCell>{loc.code}</TableCell>
                    <TableCell>{loc.name}</TableCell>
                    <TableCell><Chip size="small" label={loc.status === "A" ? "Active" : "Inactive"} color={loc.status === "A" ? "success" : "default"} /></TableCell>
                </TableRow>
                ))
            )}
            </TableBody>
        </Table>
        </TableContainer>
        <TablePagination
            component="div"
            count={filteredLocations.length}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
        />
    </Paper>

        <Snackbar open={alert.open} autoHideDuration={3000} onClose={() => setAlert((a) => ({ ...a, open: false }))}>
        <Alert severity={alert.severity} onClose={() => setAlert((a) => ({ ...a, open: false }))}>{alert.message}</Alert>
        </Snackbar>
    </Box>
    );
};

export default LocationManagement;